import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  NativeSyntheticEvent,
  TextInput,
  TextInputKeyPressEventData,
} from 'react-native'
import { ScrollView, XStack, YStack, isWeb, styled } from 'tamagui'

import { Text } from '../Text'
import { Icon } from '../Icon'

import './FormInput.css'

export type ComboboxSection<T> = {
  key: string
  /** Optional heading shown above the section's options. */
  title?: string
  items: T[]
}

const TriggerButton = styled(XStack, {
  className: 'FormInput',
  borderWidth: 1,
  borderRadius: '$2',
  borderColor: '$borderColor',
  paddingHorizontal: '$3',
  paddingVertical: '$2',
  minHeight: 38,
  alignItems: 'center',
  gap: '$3',
  cursor: 'pointer',
  backgroundColor: '$background',

  hoverStyle: {
    borderColor: '$borderColorHover',
  },

  focusStyle: {
    borderColor: '$borderColorFocus',
  },
})

const SearchInput = styled(
  TextInput,
  {
    name: 'ComboboxSearchInput',
    borderWidth: 1,
    borderRadius: '$2',
    borderColor: '$borderColor',
    placeholderTextColor: '$colorMuted',
    padding: 10,
    color: '$color',
    fontSize: 16,

    hoverStyle: {
      borderColor: '$borderColorHover',
    },

    focusStyle: {
      borderColor: '$borderColorFocus',
    },
  } as const,
  { isInput: true }
)

const DropdownContainer = styled(YStack, {
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  zIndex: 1000,
  backgroundColor: '$background',
  borderWidth: 1,
  borderColor: '$borderColor',
  borderRadius: '$2',
  marginTop: '$1',
  overflow: 'hidden',
  ...(isWeb
    ? {
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      }
    : {
        elevation: 4,
      }),
})

const OptionRow = styled(XStack, {
  paddingHorizontal: '$3',
  paddingVertical: '$2',
  minHeight: 44,
  alignItems: 'center',
  gap: '$3',
  cursor: 'pointer',

  hoverStyle: {
    backgroundColor: '$backgroundHover',
  },

  variants: {
    highlighted: {
      true: {
        backgroundColor: '$backgroundStrong',
      },
    },
  } as const,
})

/**
 * A button that opens into a search box with a list of options below it.
 * Handles opening and closing, arrow/Enter/Escape keys, dismissing on an
 * outside click, and highlighting; callers supply the options for a query
 * and how to render them.
 */
export const SearchableCombobox = <T,>({
  getSections,
  getKey,
  renderOption,
  renderTrigger,
  renderEmpty,
  onSelect,
  isSelected,
  placeholder,
  label,
  valueLabel,
  onOpen,
  onClose,
  triggerMinHeight,
  maxListHeight = 320,
}: {
  /** Options to show for the current query, grouped into sections. */
  getSections: (query: string) => ComboboxSection<T>[]
  getKey: (item: T) => string
  renderOption: (item: T) => React.ReactNode
  /** Contents of the closed trigger button. */
  renderTrigger: () => React.ReactNode
  /** Shown when the query matches nothing; nothing is shown without one. */
  renderEmpty?: (query: string) => React.ReactNode
  onSelect: (item: T) => void
  isSelected?: (item: T) => boolean
  placeholder: string
  /** Accessible name for the trigger and the search box. */
  label: string
  /** The current value, read out with the trigger's name. */
  valueLabel?: string
  onOpen?: () => void
  onClose?: () => void
  triggerMinHeight?: number
  maxListHeight?: number
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  // The search box takes the closed trigger's height, so opening the
  // picker doesn't shift the form below it.
  const [triggerHeight, setTriggerHeight] = useState<number>()

  const containerRef = useRef<React.ElementRef<typeof YStack>>(null)
  const inputRef = useRef<TextInput>(null)
  const triggerRef = useRef<HTMLElement | null>(null)
  const returnFocusRef = useRef(false)

  const baseId = useId()
  const listId = `${baseId}-list`
  const optionId = (index: number) => `${baseId}-option-${index}`

  const sections = useMemo(
    () =>
      isOpen
        ? getSections(query).filter((section) => section.items.length > 0)
        : [],
    [getSections, isOpen, query]
  )

  const flatItems = useMemo(
    () => sections.flatMap((section) => section.items),
    [sections]
  )

  const handleOpen = useCallback(() => {
    setIsOpen(true)
    setQuery('')
    setHighlightedIndex(-1)
    onOpen?.()
    setTimeout(() => {
      inputRef.current?.focus()
    }, 0)
  }, [onOpen])

  // Picking an option or pressing Escape puts focus back on the trigger;
  // clicking elsewhere leaves it where the click went.
  const handleClose = useCallback(
    (returnFocus = false) => {
      returnFocusRef.current = returnFocus
      setIsOpen(false)
      setQuery('')
      setHighlightedIndex(-1)
      onClose?.()
    },
    [onClose]
  )

  const handleSelect = useCallback(
    (item: T) => {
      onSelect(item)
      handleClose(true)
    },
    [onSelect, handleClose]
  )

  useEffect(() => {
    if (!isOpen && returnFocusRef.current) {
      returnFocusRef.current = false
      triggerRef.current?.focus?.()
    }
  }, [isOpen])

  // react-native-web's TextInput stops keydown propagation, so keyboard
  // navigation is handled on the input itself. Preventing the default on
  // Enter also stops the browser from submitting the surrounding form.
  const handleKeyPress = useCallback(
    (event: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
      switch (event.nativeEvent.key) {
        case 'ArrowDown':
          event.preventDefault()
          setHighlightedIndex((prev) =>
            prev < flatItems.length - 1 ? prev + 1 : 0
          )
          break
        case 'ArrowUp':
          event.preventDefault()
          setHighlightedIndex((prev) =>
            prev > 0 ? prev - 1 : flatItems.length - 1
          )
          break
        case 'Enter': {
          event.preventDefault()
          // Enter picks the highlighted option, or the only match.
          const item =
            flatItems[highlightedIndex] ??
            (flatItems.length === 1 ? flatItems[0] : undefined)
          if (item !== undefined) {
            handleSelect(item)
          }
          break
        }
        case 'Escape':
          event.preventDefault()
          handleClose(true)
          break
      }
    },
    [flatItems, highlightedIndex, handleSelect, handleClose]
  )

  // Click outside to dismiss (web only)
  useEffect(() => {
    if (!isWeb || !isOpen) return

    const handleClickOutside = (event: MouseEvent) => {
      const container = containerRef.current as unknown as HTMLElement | null
      if (container && !container.contains(event.target as Node)) {
        handleClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, handleClose])

  // A typed query highlights the first match so Enter picks it.
  useEffect(() => {
    setHighlightedIndex(query ? 0 : -1)
  }, [query])

  // Keep the highlighted option visible while arrowing through a long list.
  useEffect(() => {
    if (!isWeb || !isOpen || highlightedIndex < 0) return
    document
      .getElementById(`${baseId}-option-${highlightedIndex}`)
      ?.scrollIntoView({ block: 'nearest' })
  }, [baseId, highlightedIndex, isOpen])

  if (!isOpen) {
    return (
      <TriggerButton
        ref={triggerRef as never}
        onPress={handleOpen}
        onLayout={(event) => setTriggerHeight(event.nativeEvent.layout.height)}
        role='combobox'
        aria-expanded={false}
        aria-haspopup='listbox'
        aria-label={valueLabel ? `${label}: ${valueLabel}` : label}
        {...(triggerMinHeight ? { minHeight: triggerMinHeight } : {})}
        {...(isWeb
          ? {
              tabIndex: 0,
              onKeyDown: (event: React.KeyboardEvent) => {
                if (
                  event.key === 'Enter' ||
                  event.key === ' ' ||
                  event.key === 'ArrowDown'
                ) {
                  // Opening the picker must not also submit the form.
                  event.preventDefault()
                  event.stopPropagation()
                  handleOpen()
                }
              },
            }
          : {})}
      >
        {renderTrigger()}

        <Icon name='chevron-down' size='$4' color='$colorMuted' />
      </TriggerButton>
    )
  }

  let optionIndex = 0
  const empty = query && flatItems.length === 0 ? renderEmpty?.(query) : null

  return (
    <YStack
      ref={containerRef}
      position='relative'
      zIndex={1000}
      {...(isWeb ? { style: { zIndex: 1000 } } : {})}
    >
      <SearchInput
        ref={inputRef}
        className='FormInput'
        {...(triggerHeight ? { height: triggerHeight } : {})}
        value={query}
        onChangeText={setQuery}
        onKeyPress={handleKeyPress}
        placeholder={placeholder}
        aria-label={label}
        // An expanded combobox owns Escape, so a surrounding Dialog leaves
        // it alone (see DialogWrapper).
        role='combobox'
        aria-expanded
        aria-haspopup='listbox'
        aria-autocomplete='list'
        aria-controls={listId}
        aria-activedescendant={
          highlightedIndex >= 0 && highlightedIndex < flatItems.length
            ? optionId(highlightedIndex)
            : undefined
        }
        autoFocus
        autoComplete='off'
        {...(isWeb ? { style: { outline: 'none' } } : {})}
      />

      {sections.length > 0 || empty ? (
        <DropdownContainer id={listId}>
          <ScrollView maxHeight={maxListHeight}>
            {empty ? (
              <YStack padding='$3'>{empty}</YStack>
            ) : (
              sections.map((section) => (
                <YStack
                  key={section.key}
                  paddingBottom='$1'
                  {...({ role: 'listbox' } as object)}
                  aria-label={section.title ?? label}
                >
                  {section.title ? (
                    <Text
                      size='$3'
                      fontWeight='$6'
                      color='$colorMuted'
                      textTransform='uppercase'
                      letterSpacing={0.5}
                      paddingHorizontal='$3'
                      paddingTop='$3'
                      paddingBottom='$1'
                    >
                      {section.title}
                    </Text>
                  ) : null}

                  {section.items.map((item) => {
                    const index = optionIndex++
                    const selected = isSelected?.(item) ?? false

                    return (
                      <OptionRow
                        key={getKey(item)}
                        id={optionId(index)}
                        highlighted={index === highlightedIndex}
                        onPress={() => handleSelect(item)}
                        onHoverIn={() => setHighlightedIndex(index)}
                        role='option'
                        aria-selected={selected}
                      >
                        <XStack flex={1} alignItems='center' gap='$3'>
                          {renderOption(item)}
                        </XStack>

                        {selected ? (
                          <Icon
                            name='checkmark'
                            size='$5'
                            color='$primaryColor'
                          />
                        ) : null}
                      </OptionRow>
                    )
                  })}
                </YStack>
              ))
            )}
          </ScrollView>
        </DropdownContainer>
      ) : null}
    </YStack>
  )
}
